"use client";

import { useEffect, useMemo, useRef } from "react";
import { HINT } from "@/components/tool-kit/styles";
import { codeColourFor } from "@/components/tools/qualitative-text/coding-colours";
import {
  type CodingDocument,
  codePath,
  documentPieces,
  type Project,
} from "@/lib/tool-kit/qualitative-text/coding";

interface Range {
  start: number;
  end: number;
}

const PIECE_SELECTOR = "[data-offset]";

/**
 * The selection inside `root` as character offsets into the document.
 *
 * Every drawn run carries `data-offset` and `data-end`, so the offsets come from which runs the
 * selection touches, not from walking text nodes: that stays right when a selection ends in
 * the gap between two lines or on a line break.
 */
export function readSelection(root: HTMLElement): Range | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }
  const range = selection.getRangeAt(0);
  let first: HTMLElement | null = null;
  let last: HTMLElement | null = null;
  for (const piece of root.querySelectorAll<HTMLElement>(PIECE_SELECTOR)) {
    if (range.intersectsNode(piece)) {
      first ??= piece;
      last = piece;
    }
  }
  if (!(first && last)) {
    return null;
  }
  const startBase = Number(first.dataset.offset);
  const endBase = Number(last.dataset.offset);
  const start =
    first.contains(range.startContainer) &&
    range.startContainer.nodeType === Node.TEXT_NODE
      ? startBase + range.startOffset
      : startBase;
  const end =
    last.contains(range.endContainer) &&
    range.endContainer.nodeType === Node.TEXT_NODE
      ? endBase + range.endOffset
      : Number(last.dataset.end);
  return end > start ? { start, end } : null;
}

interface DocumentStrings {
  documentLabel: string;
  codeParagraph: string;
  noCode: string;
}

export function CodingDocumentPane({
  doc,
  project,
  strings,
  canCode,
  onSelection,
  onCodeLine,
}: {
  doc: CodingDocument;
  project: Project;
  strings: DocumentStrings;
  canCode: boolean;
  onSelection: (range: Range | null) => void;
  onCodeLine: (range: Range) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const segments = useMemo(
    () => project.segments.filter((seg) => seg.docId === doc.id),
    [project.segments, doc.id]
  );
  const pieces = useMemo(
    () => documentPieces(doc.text.length, segments),
    [doc.text.length, segments]
  );
  const lines = useMemo(() => {
    const found: Range[] = [];
    let start = 0;
    for (const line of doc.text.split("\n")) {
      found.push({ start, end: start + line.length });
      start += line.length + 1;
    }
    return found;
  }, [doc.text]);

  // Native listeners: the text is a reading surface, and a selection is made by
  // the browser itself, so there is nothing for the element to announce as a control.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const handle = () => onSelection(readSelection(root));
    root.addEventListener("mouseup", handle);
    root.addEventListener("keyup", handle);
    return () => {
      root.removeEventListener("mouseup", handle);
      root.removeEventListener("keyup", handle);
    };
  }, [onSelection]);

  return (
    <section
      aria-label={strings.documentLabel}
      className="max-h-[560px] space-y-1 overflow-y-auto rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed"
    >
      <div ref={rootRef}>
        {lines.map((line) => (
          <div className="group flex items-start gap-2" key={line.start}>
            <p className="min-h-[1.5em] flex-1 whitespace-pre-wrap break-words">
              {pieces
                .filter((p) => p.end > line.start && p.start < line.end)
                .map((piece) => {
                  const a = Math.max(piece.start, line.start);
                  const b = Math.min(piece.end, line.end);
                  const text = doc.text.slice(a, b);
                  if (piece.segmentIds.length === 0) {
                    return (
                      <span data-end={b} data-offset={a} key={a}>
                        {text}
                      </span>
                    );
                  }
                  const covering = segments.filter((s) =>
                    piece.segmentIds.includes(s.id)
                  );
                  const first = covering[0];
                  const colour = codeColourFor(project, first?.codeId ?? "");
                  return (
                    <mark
                      className={`rounded px-0.5 text-foreground ${colour.mark} ${covering.length > 1 ? "underline decoration-2 decoration-foreground/40" : ""}`}
                      data-end={b}
                      data-offset={a}
                      key={a}
                      title={covering
                        .map((s) => codePath(project, s.codeId))
                        .join("; ")}
                    >
                      {text}
                    </mark>
                  );
                })}
            </p>
            {canCode && line.end > line.start ? (
              <button
                aria-label={strings.codeParagraph}
                className="select-none rounded border border-border px-1.5 text-muted-foreground text-xs opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
                onClick={() => onCodeLine(line)}
                title={strings.codeParagraph}
                type="button"
              >
                +
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {canCode ? null : <p className={HINT}>{strings.noCode}</p>}
    </section>
  );
}
